import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from "@/components/ui/sidebar";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useGetUserDetails } from "../../hooks/useGetUserDetails";
import { useSupportChatAvailable } from "../../hooks/useSupportChatAvailable";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { Feature as PlanFeature } from "../../types/entitlements";
import { AgentsButton } from "../../features/agentAttention/components/AgentsButton";
import { useSkipOnboardingGuard } from "../../features/onboarding/hooks/useSkipOnboardingGuard";
import { NotificationsBell } from "../../features/notifications/NotificationsBell";
import { TrackerChip } from "../../features/timeTracking/tracker/TrackerChip";
import { useTimeNav } from "../../features/timeTracking/useTimeNav";
import { NavSupport } from "./NavSupport";
import { NavUser } from "./NavUser";
import { SidebarBrand } from "./SidebarBrand";
import { buildNavItems, isNavItemActive, type NavItem } from "./navItems";
import { hideSidebarTabs } from "./sidebarTabs";
import { useHiddenSidebarTabs } from "./useHiddenSidebarTabs";

function NavMenu({ items }: { items: NavItem[] }) {
  const { pathname } = useLocation();
  return (
    <SidebarMenu>
      {items.map((item) => {
        const active = isNavItemActive(item, pathname);
        return (
          <SidebarMenuItem key={item.id}>
            <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
              <Link to={item.url}>
                <item.icon />
                <span className="truncate">{item.label}</span>
              </Link>
            </SidebarMenuButton>
            {item.badge ? (
              <SidebarMenuBadge className="px-0">
                <StatusBadge tone="accent">{item.badge}</StatusBadge>
              </SidebarMenuBadge>
            ) : item.count ? (
              <SidebarMenuBadge
                data-testid={`nav-count-${item.id}`}
                className={item.warn ? "px-0" : undefined}
              >
                {item.warn ? (
                  <StatusBadge tone="warn">{item.count}</StatusBadge>
                ) : (
                  item.count
                )}
              </SidebarMenuBadge>
            ) : null}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

/**
 * App-wide left navigation (shadcn Sidebar, collapses to an icon rail).
 * `guardNavigation` (the guided first run) asks before a link leaves the page.
 */
export function AppSidebar({
  guardNavigation = false
}: {
  guardNavigation?: boolean;
}) {
  const {
    userDetails: { userType, role, visaType, id }
  } = useGetUserDetails();
  const { hasFeature } = useEntitlements();
  // Subscribe so nav re-evaluates when beta features load.
  useSelector(lawyerSelectors.selectBetaFeatures);

  const hasPlaygroundAccess =
    import.meta.env.REACT_APP_ENABLE_PLAYGROUND === "true" &&
    hasFeature(PlanFeature.ATHENA);
  const time = useTimeNav(userType === "Lawyer" && Boolean(id));
  const hiddenTabs = useHiddenSidebarTabs(userType === "Lawyer" && Boolean(id));

  const nav = useMemo(() => {
    const sections = buildNavItems({
      userType,
      role,
      uid: id,
      visaLabel: visaType || undefined,
      hasPlaygroundAccess,
      time
    });
    return { ...sections, main: hideSidebarTabs(sections.main, hiddenTabs) };
  }, [userType, role, id, visaType, hasPlaygroundAccess, time, hiddenTabs]);

  const isAuthed = Boolean(id);
  // What's coming due, team mentions and agents are lawyer-side features.
  const showNotifications = isAuthed && userType === "Lawyer";
  const supportChat = useSupportChatAvailable();
  const showSupport = isAuthed && supportChat;
  const guard = useSkipOnboardingGuard(guardNavigation);

  return (
    <Sidebar
      collapsible="icon"
      style={{
        top: "var(--app-strip-h, 0px)",
        height: "calc(100svh - var(--app-strip-h, 0px))"
      }}
    >
      <div className="contents" onClickCapture={guard.onClickCapture}>
        {/* Brand tile: same height and rule as the app tab bar (36px, line-2) so the lines meet.
            On the rail it shares the menu groups' px-2.5, as does the footer, so the mark,
            icons and avatar sit on one column. */}
        <SidebarHeader className="h-9 shrink-0 flex-row items-center border-b border-line-2 p-0 px-1 group-data-[collapsible=icon]:px-2.5">
          <SidebarBrand />
        </SidebarHeader>
        <SidebarContent>
          {nav.main.length ? (
            <SidebarGroup className="group-data-[collapsible=icon]:px-2.5">
              <SidebarGroupContent>
                <NavMenu items={nav.main} />
              </SidebarGroupContent>
            </SidebarGroup>
          ) : null}

          {nav.internal.length ? (
            <SidebarGroup className="mt-auto group-data-[collapsible=icon]:px-2.5">
              <SidebarGroupLabel>Internal</SidebarGroupLabel>
              <SidebarGroupContent>
                <NavMenu items={nav.internal} />
              </SidebarGroupContent>
            </SidebarGroup>
          ) : null}

          {showNotifications || showSupport ? (
            <SidebarGroup
              className={cn(
                "group-data-[collapsible=icon]:px-2.5",
                !nav.internal.length && "mt-auto"
              )}
            >
              <SidebarGroupContent>
                <SidebarMenu>
                  {showNotifications ? (
                    <>
                      <TrackerChip />
                      <NotificationsBell />
                      <AgentsButton />
                    </>
                  ) : null}
                  {showSupport ? <NavSupport /> : null}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ) : null}
        </SidebarContent>
        {isAuthed ? (
          <SidebarFooter className="h-(--app-footer-h) shrink-0 justify-center group-data-[collapsible=icon]:px-2.5">
            <NavUser />
          </SidebarFooter>
        ) : null}
      </div>
      {guard.dialog}
      <SidebarRail />
    </Sidebar>
  );
}
